"use client";
import { useState, useEffect } from "react";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import { Calendar, Users, Target, HeadphonesIcon } from "lucide-react";

export default function StatsCounter() {
  const [stats, setStats] = useState([
    { id: 1, value: 10, suffix: "+", label: "Жилийн туршлага", icon: Calendar },
    { id: 2, value: 500, suffix: "+", label: "Харилцагчид", icon: Users },
    { id: 3, value: 1200, suffix: "+", label: "Амжилттай төслүүд", icon: Target },
    { id: 4, value: 24, suffix: "/7", label: "Дэмжлэгийн үйлчилгээ", icon: HeadphonesIcon },
  ]);
  const [loading, setLoading] = useState(true);

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch("/api/stats");
        if (res.ok) {
          const data = await res.json();
          // Map fetched data with icons
          const icons = [Calendar, Users, Target, HeadphonesIcon];
          const mappedData = data.map((stat, index) => ({
            ...stat,
            icon: icons[index] || icons[0],
          }));
          setStats(mappedData);
        }
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="bg-[#577099] py-16 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-32 bg-white rounded-xl shadow-sm animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#577099] py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <div
          ref={ref}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
        >
          {stats.map((item) => {
            const IconComponent = item.icon;
            return (
              <div
                key={item.id}
                className="group relative flex flex-col items-center justify-center p-6 md:p-8 bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 overflow-hidden"
              >
                {/* Background gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#577099]/5 to-[#4a5f85]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative z-10 flex flex-col items-center">
                  {/* Icon */}
                  <div className="mb-4 p-3 bg-gradient-to-br from-[#577099]/10 to-[#4a5f85]/10 rounded-full group-hover:from-[#577099]/20 group-hover:to-[#4a5f85]/20 transition-all duration-300">
                    <IconComponent className="w-6 h-6 md:w-7 md:h-7 text-[#577099] group-hover:scale-110 transition-transform duration-300" />
                  </div>

                  {/* Animated number */}
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#577099] mb-2">
                    {inView && (
                      <CountUp
                        end={item.value}
                        duration={2.5}
                        suffix={item.suffix}
                        separator=","
                      />
                    )}
                    {!inView && `0${item.suffix}`}
                  </h2>

                  {/* Label */}
                  <p className="text-gray-700 text-sm md:text-base text-center font-semibold">
                    {item.label}
                  </p>
                </div>

                {/* Decorative bottom border */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#577099]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
